import type { AnnotationRange } from "./textAnnotations"

const WRAPPER_ATTR = "data-quiz-annotation-wrapper"

function unwrapElement(el: Element) {
  const parent = el.parentNode
  if (!parent) return
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el)
  }
  parent.removeChild(el)
}

export function clearDecorations(root: HTMLElement) {
  const wrappers = root.querySelectorAll(`[${WRAPPER_ATTR}]`)
  for (const wrapper of wrappers) {
    unwrapElement(wrapper)
  }
}

export function getSelectionOffsetsWithin(root: HTMLElement) {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return null

  const range = selection.getRangeAt(0)
  if (range.collapsed) return null

  const common = range.commonAncestorContainer
  const commonElement =
    common.nodeType === Node.ELEMENT_NODE
      ? (common as Element)
      : common.parentElement
  if (!commonElement) return null
  if (commonElement !== root && !root.contains(commonElement)) return null

  if (
    !root.contains(range.startContainer) ||
    !root.contains(range.endContainer)
  ) {
    return null
  }

  const start = getRawTextOffsetWithin(
    root,
    range.startContainer,
    range.startOffset,
  )
  const end = getRawTextOffsetWithin(root, range.endContainer, range.endOffset)

  if (start === end) return null
  return start < end ? { start, end } : { start: end, end: start }
}

function getRawTextOffsetWithin(
  root: HTMLElement,
  container: Node,
  offset: number,
) {
  const boundary = document.createRange()
  boundary.selectNodeContents(root)
  boundary.setEnd(container, offset)

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let total = 0

  while (walker.nextNode()) {
    const node = walker.currentNode as Text
    const nodeLen = node.nodeValue?.length ?? 0
    if (nodeLen === 0) continue

    // If this node starts after the boundary, we're done.
    if (boundary.comparePoint(node, 0) === 1) break

    // If the whole node is within the boundary, count it.
    if (boundary.comparePoint(node, nodeLen) === 0) {
      total += nodeLen
      continue
    }

    // Partial coverage can only happen when the boundary ends inside this node.
    if (boundary.endContainer === node) {
      total += Math.max(0, Math.min(boundary.endOffset, nodeLen))
    }
    break
  }

  return total
}

function segmentHasKind(params: {
  kind: AnnotationRange["kind"]
  start: number
  end: number
  ranges: AnnotationRange[]
}) {
  return params.ranges.some(
    (r) =>
      r.kind === params.kind && r.start <= params.start && r.end >= params.end,
  )
}

export function decorateByOffsets(params: {
  root: HTMLElement
  ranges: AnnotationRange[]
}) {
  clearDecorations(params.root)
  if (params.ranges.length === 0) return

  const textNodes: Text[] = []
  const walker = document.createTreeWalker(params.root, NodeFilter.SHOW_TEXT)
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text)
  }

  if (textNodes.length === 0) return

  let globalOffset = 0
  for (const textNode of textNodes) {
    const value = textNode.nodeValue ?? ""
    if (!value) continue

    const nodeStart = globalOffset
    const nodeEnd = nodeStart + value.length
    globalOffset = nodeEnd

    const boundaries = new Set<number>([0, value.length])
    for (const range of params.ranges) {
      const overlapStart = Math.max(nodeStart, range.start)
      const overlapEnd = Math.min(nodeEnd, range.end)
      if (overlapStart < overlapEnd) {
        boundaries.add(overlapStart - nodeStart)
        boundaries.add(overlapEnd - nodeStart)
      }
    }

    const points = [...boundaries].sort((a, b) => a - b)

    // If there are no internal boundaries but the whole node is covered, wrap it.
    if (points.length <= 2) {
      const highlight = segmentHasKind({
        kind: "highlight",
        start: nodeStart,
        end: nodeEnd,
        ranges: params.ranges,
      })
      const strike = segmentHasKind({
        kind: "strike",
        start: nodeStart,
        end: nodeEnd,
        ranges: params.ranges,
      })
      if (!highlight && !strike) continue

      const span = document.createElement("span")
      span.setAttribute(WRAPPER_ATTR, "true")
      span.style.setProperty("box-decoration-break", "clone")
      span.style.setProperty("-webkit-box-decoration-break", "clone")
      if (highlight) {
        span.style.backgroundColor = "rgba(255, 230, 0, 0.55)"
        span.style.borderRadius = "2px"
        span.style.padding = "0 1px"
      }
      if (strike) {
        span.style.textDecoration = "line-through"
        span.style.textDecorationThickness = "2px"
      }
      span.appendChild(document.createTextNode(value))
      textNode.parentNode?.replaceChild(span, textNode)
      continue
    }

    const fragment = document.createDocumentFragment()
    for (let i = 0; i < points.length - 1; i++) {
      const segStartLocal = points[i]!
      const segEndLocal = points[i + 1]!
      if (segEndLocal <= segStartLocal) continue

      const segText = value.slice(segStartLocal, segEndLocal)
      const segStart = nodeStart + segStartLocal
      const segEnd = nodeStart + segEndLocal

      const highlight = segmentHasKind({
        kind: "highlight",
        start: segStart,
        end: segEnd,
        ranges: params.ranges,
      })
      const strike = segmentHasKind({
        kind: "strike",
        start: segStart,
        end: segEnd,
        ranges: params.ranges,
      })

      if (!highlight && !strike) {
        fragment.appendChild(document.createTextNode(segText))
        continue
      }

      const span = document.createElement("span")
      span.setAttribute(WRAPPER_ATTR, "true")
      span.style.setProperty("box-decoration-break", "clone")
      span.style.setProperty("-webkit-box-decoration-break", "clone")

      if (highlight) {
        span.style.backgroundColor = "rgba(255, 230, 0, 0.55)"
        span.style.borderRadius = "2px"
        span.style.padding = "0 1px"
      }
      if (strike) {
        span.style.textDecoration = "line-through"
        span.style.textDecorationThickness = "2px"
      }

      span.appendChild(document.createTextNode(segText))
      fragment.appendChild(span)
    }

    textNode.parentNode?.replaceChild(fragment, textNode)
  }
}
